import { setup, assign, fromPromise } from "xstate"
import type {
    CreateSensorPayload,
    UpdateSensorPayload,
    Source,
    SourceLegacyChannel,
    SourceLegacy,
    SourceLegacyOwnerCompany,
} from "@types"
import type { SensorV2Response, Services } from "@services"
import { OwnerOutput } from "@core/composables"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Modèle de capteur disponible
 */
export interface SensorModel {
    name: string
    brand?: string
    [key: string]: unknown
}

/**
 * Propriétaire du capteur. Toujours une organisation
 */
export interface SensorOwner {
    relation_type: "company"
    company: Omit<SourceLegacyOwnerCompany, "relation_type">
}

/**
 * Données partielles du capteur en cours de création/édition
 */
export interface SensorFormData {
    idsource?: string
    idtype?: string
    operator?: string
    apikey?: string
    brand?: string
    model?: string
    models?: SensorModel[]
    channels?: (SourceLegacyChannel & { disabled?: boolean })[]
    name?: string
    owner?: OwnerOutput | null
    startDate?: string
    endDate?: string
    floor?: string
    description?: string
}

/**
 * Input pour initialiser la machine
 */
export interface SensorFormInput {
    /**
     * Identifiant du capteur existant (mode édition)
     */
    sourceId?: string
    /**
     * Identifiant du bâtiment
     */
    buildingId?: string
    /**
     * Adresse du capteur
     */
    address?: Record<string, unknown>
    /**
     * Source existante à éditer
     */
    existingSource?: Source | null
}

/**
 * Contexte de la machine à états du formulaire de capteur
 */
export interface SensorFormContext {
    /**
     * La source en cours de création/modification
     */
    source: SensorFormData | null
    /**
     * Message d'erreur
     */
    error: string | null
    /**
     * Input de la machine (stocké pour accès dans les guards/actions)
     */
    input: SensorFormInput
}

/**
 * Résultat de la recherche de capteur
 */
export interface SearchTaskResult {
    idsource?: string
    idtype?: string
    operator?: string
    apikey?: string
    models?: SensorModel[]
}

/**
 * Données saisies manuellement
 */
export interface ManualInputData {
    idsource?: string
    idtype?: string
    operator?: string
    apikey?: string
}

/**
 * Données du formulaire d'information
 */
export interface InformationInputData {
    brand?: string
    model?: string
    channels?: SourceLegacyChannel & { disabled?: boolean }[]
    name?: string
    owner?: OwnerOutput
    startDate?: string
    endDate?: string
    floor?: string
    description?: string
}

/**
 * Événements de la machine à états
 */
export type SensorFormEvent =
    | { type: "START" }
    | { type: "INPUT_IDSOURCE"; value: string }
    | { type: "SEARCH_TASK_RESULT"; value: SearchTaskResult }
    | { type: "REDIRECT_MANUALLY" }
    | { type: "INPUT_MANUALLY"; value: ManualInputData }
    | { type: "INPUT_INFORMATION"; value: InformationInputData }
    | { type: "MODIFY_OPERATOR" }
    | { type: "CANCEL" }

/**
 * Résultat de la création/mise à jour du capteur
 */
export interface UpdateSourceResult {
    source: Source
}

// ================================
// HELPERS
// ================================

/**
 * Extrait les données de la source existante pour initialiser le formulaire
 */
function extractSourceData(
    existingSource: Source | null | undefined
): SensorFormData | null {
    if (!existingSource) return null

    return {
        idsource: existingSource.idsource,
        idtype: existingSource.idtype || "",
        operator: existingSource.operator || "",
        model: existingSource.details?.model || "",
        brand: existingSource.details?.brand || "",
        channels: existingSource.channels || [],
        name: existingSource.name,
        owner: null,
    }
}

/**
 * Vérifie si les informations du propriétaire sont vides
 */
function isOwnerValid(owner: OwnerOutput | undefined): boolean {
    if (!owner) return false

    const hasCompanyValid =
        owner?.company?._id ||
        (owner?.company?.name &&
            owner?.company?.address &&
            owner?.company?.type &&
            owner?.company?.idcompany)
    const hasUserValid =
        owner?.user?._id ||
        (owner?.user?.firstname &&
            owner?.user?.lastname &&
            owner?.user?.username)
    return !!hasCompanyValid && !!hasUserValid
}

// ================================
// MACHINE FACTORY
// ================================

/**
 * Crée la machine à états pour le formulaire de capteur
 * @param services - Services injectés pour les appels API
 * @returns La machine XState configurée
 */
export const useSensorFormMachine = ({
    services,
    onDone,
    onCancel,
    onUpdateSensor,
    onFollowSensorTask,
}: {
    services: Services | undefined
    onDone?: (source: Source) => void
    onCancel?: () => void
    onUpdateSensor?: (source: SourceLegacy) => void
    /** Appelé quand l'API renvoie meta.should_launch_getlastdata_task: true ; reçoit la réponse brute { data, meta } */
    onFollowSensorTask?: (output: SensorV2Response) => void
}) => {
    const machine = setup({
        types: {
            input: {} as SensorFormInput,
            context: {} as SensorFormContext,
            events: {} as SensorFormEvent,
        },
        guards: {
            /**
             * Vérifie si le formulaire est en mode édition (sourceId présent)
             */
            isEditMode: ({ context }) => !!context.input?.sourceId,
        },
        actions: {
            /**
             * Initialise la source à partir des données existantes
             */
            initSource: assign({
                source: ({ context }) =>
                    extractSourceData(context.input?.existingSource),
            }),
            /**
             * Met à jour le champ idsource
             */
            setIdSource: assign({
                source: ({ context, event }) => {
                    if (event.type !== "INPUT_IDSOURCE") return context.source
                    return {
                        ...context.source,
                        idsource: event.value ?? "",
                    }
                },
            }),
            /**
             * Met à jour les informations de la source selon le résultat de la tâche de recherche
             */
            setSearchTaskResult: assign({
                source: ({ context, event }) => {
                    if (event.type !== "SEARCH_TASK_RESULT")
                        return context.source
                    const { value } = event
                    return {
                        ...context.source,
                        idsource:
                            value?.idsource ?? context.source?.idsource ?? "",
                        idtype: value?.idtype ?? "",
                        operator: value?.operator ?? "",
                        apikey: value?.apikey ?? "",
                        models: value?.models ?? [],
                    }
                },
            }),
            /**
             * Met à jour les informations de la source selon les informations manuellement saisies
             */
            setManualInput: assign({
                source: ({ context, event }) => {
                    if (event.type !== "INPUT_MANUALLY") return context.source
                    const { value } = event
                    return {
                        ...context.source,
                        idsource: value?.idsource ?? "",
                        idtype: value?.idtype ?? "",
                        operator: value?.operator ?? "",
                        apikey: value?.apikey ?? "",
                    }
                },
            }),
            /**
             * Met à jour les informations complètes de la source
             */
            setSourceInformation: assign({
                source: ({ context, event }) => {
                    if (event.type !== "INPUT_INFORMATION")
                        return context.source
                    return {
                        ...context.source,
                        ...event.value,
                    }
                },
            }),
            /**
             * Réinitialise l'erreur
             */
            clearError: assign({
                error: () => null,
            }),
            /**
             * Définit l'erreur depuis l'événement d'erreur de l'acteur
             */
            setError: assign({
                error: (_, params: { error?: Error }) => {
                    return params?.error?.message || "Une erreur est survenue"
                },
            }),
            /**
             * Appelle le callback onDone
             */
            callOnDone: ({ context }) => {
                if (onDone && context.source) {
                    onDone(context.source as unknown as Source)
                }
            },
            /**
             * Appelle le callback onCancel
             */
            callOnCancel: () => {
                onCancel?.()
            },
            /**
             * Si l'API a renvoyé meta.should_launch_getlastdata_task: true, appelle le callback avec la réponse { data, meta }
             */
            followSensorTaskIfPresent: (
                _,
                params: { output?: SensorV2Response }
            ) => {
                const output = params?.output
                if (
                    onFollowSensorTask &&
                    output?.meta?.should_launch_getlastdata_task
                ) {
                    onFollowSensorTask(output)
                }
            },
            /**
             * Appelle le callback onUpdateSensor avec la source (data) créée/mise à jour
             */
            callOnUpdateSensor: (_, params: { output?: SensorV2Response }) => {
                if (onUpdateSensor && params?.output?.data) {
                    onUpdateSensor(params.output.data)
                }
            },
        },
        actors: {
            /**
             * Acteur pour créer ou mettre à jour le capteur
             */
            updateSource: fromPromise(
                async ({
                    input,
                }: {
                    input: {
                        source: SensorFormData
                        sourceId?: string
                        buildingId?: string
                        address?: Record<string, unknown>
                    }
                }) => {
                    const { source, sourceId, buildingId, address } = input

                    const channels =
                        source.channels?.filter((c) => !c.disabled) ?? []

                    // Utilitaire pour l'objet owner (DRY)
                    const buildOwner = (owner?: OwnerOutput) =>
                        isOwnerValid(owner)
                            ? {
                                  _id_user: owner?.user?._id,
                                  firstname: owner?.user?.firstname,
                                  lastname: owner?.user?.lastname,
                                  username: owner?.user?.username,
                                  company: {
                                      _id_company: owner?.company?._id,
                                      name: owner?.company?.name || "",
                                      address: owner?.company?.address || null,
                                      type: owner?.company?.type || "",
                                      idcompany: owner?.company?.idcompany,
                                      legalForm: owner?.company?.legalForm,
                                  },
                              }
                            : undefined

                    let payload: CreateSensorPayload | UpdateSensorPayload

                    if (!sourceId) {
                        // Création
                        payload = {
                            idsource: source.idsource || "",
                            idtype: source.idtype || "",
                            operator: source.operator || "",
                            apikey: source.apikey,
                            brand: source.brand || "",
                            model: source.model || "",
                            channels,
                            name: source.name || "",
                            address: address as CreateSensorPayload["address"],
                            _id_building: buildingId,
                            start_date: source.startDate,
                            end_date: source.endDate,
                            floor: source.floor,
                            description: source.description,
                        }
                        const owner = buildOwner(
                            source?.owner as OwnerOutput | undefined
                        )
                        if (owner)
                            (payload as CreateSensorPayload).owner = owner
                        return await services?.$createSensor(
                            payload as CreateSensorPayload
                        )
                    } else {
                        // Mise à jour
                        payload = {
                            idsource: source.idsource,
                            operator: source.operator,
                            apikey: source.apikey,
                            brand: source.brand,
                            model: source.model,
                            channels,
                            name: source.name,
                            _id_building: buildingId,
                        }
                        const owner = buildOwner(
                            source?.owner as OwnerOutput | undefined
                        )
                        if (owner)
                            (payload as UpdateSensorPayload).owner = owner
                        return await services?.$updateSensor(
                            sourceId,
                            payload as UpdateSensorPayload
                        )
                    }
                }
            ),
        },
    }).createMachine({
        id: "sensor-form",
        initial: "idle",
        context: ({ input }) => ({
            source: null,
            error: null,
            input: input || {},
        }),
        states: {
            /**
             * État initial - Attente du démarrage
             */
            idle: {
                on: {
                    START: [
                        {
                            target: "SensorFormInformation",
                            guard: "isEditMode",
                            actions: "initSource",
                        },
                        {
                            target: "SensorFormSearchTask",
                        },
                    ],
                },
            },
            /**
             * État de recherche du capteur par ID source
             */
            SensorFormSearchTask: {
                on: {
                    INPUT_IDSOURCE: {
                        actions: "setIdSource",
                    },
                    SEARCH_TASK_RESULT: {
                        target: "SensorFormInformation",
                        actions: "setSearchTaskResult",
                    },
                    REDIRECT_MANUALLY: {
                        target: "SensorFormSearchManually",
                    },
                },
            },
            /**
             * État de saisie manuelle des informations
             */
            SensorFormSearchManually: {
                on: {
                    INPUT_MANUALLY: {
                        target: "SensorFormInformation",
                        actions: "setManualInput",
                    },
                    CANCEL: {
                        target: "SensorFormSearchTask",
                    },
                },
            },
            /**
             * État de saisie des informations complètes du capteur
             */
            SensorFormInformation: {
                on: {
                    INPUT_INFORMATION: {
                        target: "loading",
                        actions: "setSourceInformation",
                    },
                    MODIFY_OPERATOR: {
                        target: "SensorFormSearchTask",
                        guard: ({ context }) => !context.input?.sourceId,
                    },
                    CANCEL: [
                        {
                            target: "cancelled",
                            guard: "isEditMode",
                        },
                        {
                            target: "SensorFormSearchTask",
                        },
                    ],
                },
            },
            /**
             * État de chargement - Création/mise à jour en cours
             */
            loading: {
                entry: "clearError",
                invoke: {
                    id: "updateSource",
                    src: "updateSource",
                    input: ({ context }) => ({
                        source: context.source || {},
                        sourceId: context.input?.sourceId,
                        buildingId: context.input?.buildingId,
                        address: context.input?.address,
                    }),
                    onDone: {
                        target: "done",
                        actions: [
                            {
                                type: "followSensorTaskIfPresent",
                                params: ({ event }) => ({
                                    output: event.output as SensorV2Response,
                                }),
                            },
                            {
                                type: "callOnUpdateSensor",
                                params: ({ event }) => ({
                                    output: event.output as SensorV2Response,
                                }),
                            },
                        ],
                    },
                    onError: {
                        target: "SensorFormInformation",
                        actions: {
                            type: "setError",
                            params: ({ event }) => ({
                                error: event.error as Error | undefined,
                            }),
                        },
                    },
                },
            },
            /**
             * État annulé (mode édition uniquement)
             */
            cancelled: {
                type: "final",
                entry: "callOnCancel",
            },
            /**
             * État final - Opération terminée avec succès
             */
            done: {
                type: "final",
                entry: "callOnDone",
            },
        },
    })

    return {
        machine,
    }
}

/**
 * Type de la machine exportée
 */
export type SensorFormMachine = ReturnType<
    typeof useSensorFormMachine
>["machine"]
